/**
 * Authenticated encryption of small JSON documents under a caller-derived key: dependency-free and synchronous like
 * ./hmac.ts, because it has to run in every backend mode (the in-browser backend has no node:crypto, and crypto.subtle
 * is async-only). The backend seals the stored replies of its idempotency cache with it (src/server/core/backend.ts):
 * a sign-in reply holds tokens, and the data directory must never hold a usable token.
 *
 * Construction (encrypt-then-MAC, HMAC-SHA256 as the pseudo-random function):
 *   encKey = HMAC(key, "fwp-seal-enc"), macKey = HMAC(key, "fwp-seal-mac");
 *   keystream block i = HMAC(encKey, nonce || uint32be(i)); ciphertext = UTF-8(JSON) XOR keystream;
 *   tag = HMAC(macKey, nonce || ciphertext).
 * Sealed text: `s1.` + hex(nonce(16) || tag(32) || ciphertext). A wrong key, a changed byte or another format opens as
 * null. The nonce must be random (a key may seal several values).
 */
import { HmacSha256 } from './hmac';
import { toHex } from './sha256';
import { utf8Encode } from '../json';

const PREFIX = 's1.';
const NONCE_BYTES = 16;
const TAG_BYTES = 32;

function keys(key: string | Uint8Array): { enc: HmacSha256; mac: HmacSha256 } {
  const root = new HmacSha256(key);
  return { enc: new HmacSha256(root.digest('fwp-seal-enc')), mac: new HmacSha256(root.digest('fwp-seal-mac')) };
}

function xorKeystream(enc: HmacSha256, nonce: Uint8Array, data: Uint8Array): Uint8Array {
  const out = new Uint8Array(data.length);
  const block = new Uint8Array(nonce.length + 4);
  block.set(nonce);
  for (let i = 0, off = 0; off < data.length; i++, off += 32) {
    block[nonce.length] = (i >>> 24) & 0xff;
    block[nonce.length + 1] = (i >>> 16) & 0xff;
    block[nonce.length + 2] = (i >>> 8) & 0xff;
    block[nonce.length + 3] = i & 0xff;
    const ks = enc.digest(block);
    for (let j = 0; j < 32 && off + j < data.length; j++) out[off + j] = data[off + j] ^ ks[j];
  }
  return out;
}

function concat(...parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

function fromHex(hex: string): Uint8Array | null {
  if (hex.length % 2 !== 0 || !/^[0-9a-f]*$/.test(hex)) return null;
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/** Constant-time comparison of two byte strings. */
function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let d = 0;
  for (let i = 0; i < a.length; i++) d |= a[i] ^ b[i];
  return d === 0;
}

/** Seals `value` (a JSON value) under `key`; `nonce` = 16 random bytes. */
export function sealJson(key: string | Uint8Array, value: unknown, nonce: Uint8Array): string {
  if (nonce.length !== NONCE_BYTES) throw new Error(`sealJson: the nonce must have ${NONCE_BYTES} bytes`);
  const text = JSON.stringify(value);
  if (text === undefined) throw new Error('sealJson: the value is not a JSON value');
  const k = keys(key);
  const ciphertext = xorKeystream(k.enc, nonce, utf8Encode(text));
  const tag = k.mac.digest(concat(nonce, ciphertext));
  return PREFIX + toHex(concat(nonce, tag, ciphertext));
}

/** The value sealed under `key`, or null (another key, a changed or malformed text). */
export function openSealedJson<T = unknown>(key: string | Uint8Array, sealed: string): T | null {
  if (typeof sealed !== 'string' || !sealed.startsWith(PREFIX)) return null;
  const bytes = fromHex(sealed.slice(PREFIX.length));
  if (!bytes || bytes.length < NONCE_BYTES + TAG_BYTES) return null;
  const nonce = bytes.subarray(0, NONCE_BYTES);
  const tag = bytes.subarray(NONCE_BYTES, NONCE_BYTES + TAG_BYTES);
  const ciphertext = bytes.subarray(NONCE_BYTES + TAG_BYTES);
  const k = keys(key);
  if (!sameBytes(k.mac.digest(concat(nonce, ciphertext)), tag)) return null;
  try {
    return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(xorKeystream(k.enc, nonce, ciphertext))) as T;
  } catch {
    return null;
  }
}
